import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, TrendingUp } from "lucide-react";

interface NoDegreeIntroProps {
  locationName: string;
  isEntryLevel?: boolean;
}

/**
 * Introduction content for no-degree and entry-level job pages
 */
export default function NoDegreeIntro({ 
  locationName, 
  isEntryLevel = false 
}: NoDegreeIntroProps) {
  const title = isEntryLevel 
    ? `Best Entry-Level Jobs Without a Degree in ${locationName}`
    : `Best No-Degree Jobs in ${locationName}`;
  
  const introText = isEntryLevel
    ? `Starting your career without a college degree doesn't mean limiting your earning potential. ` +
      `${locationName} offers numerous entry-level opportunities that provide competitive salaries, ` +
      `career growth potential, and on-the-job training. The occupations listed below are ideal for ` +
      `new graduates, career changers, and those entering the workforce for the first time.`
    : `You don't need a college degree to build a successful, high-paying career in ${locationName}. ` +
      `Many skilled trades, technical roles, and service positions offer competitive salaries and ` +
      `excellent growth opportunities. The occupations below are based on Bureau of Labor Statistics ` +
      `data and represent the highest-paying jobs that typically don't require a bachelor's degree.`;

  const growthText = `These roles often provide opportunities for advancement through experience, certifications, ` +
    `and specialized training. Many employers offer on-the-job training, apprenticeship programs, or ` +
    `tuition assistance for further education if you choose to pursue additional qualifications later.`;

  const getStartedText = `To get started, research the specific requirements for your target occupation, ` +
    `including any necessary licenses, certifications, or training programs. Many of these roles offer ` +
    `immediate employment opportunities with competitive starting salaries.`;

  return (
    <div className="space-y-6 mb-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed mb-4">
            {introText}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Career Growth Potential
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">
            {growthText}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How to Get Started</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">
            {getStartedText}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

